import {useEffect, useRef, useState} from "react";
import {isCancelledError, useQuery} from "react-query";
import axios from "axios";
import {useSnackbar} from "notistack";
import {MAX_PRESERVED_REFRESH_FAILURES} from "../../Performance/PerformanceRefreshPolicy";
import {
    DailyOverviewTotalizersPayload,
    isValidDailyOverviewTotalizersPayload,
} from "../components/DailyOverviewTotalizersFormatters";

const API_URL = "api/power-station/performance-analysis/daily-totalizers";

export const DAILY_OVERVIEW_TOTALIZERS_QUERY_KEY_ROOT = "daily-overview-totalizers";

export const normalizePowerStationIds = (ids: Array<number | string>): number[] =>
    Array.from(new Set(ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id)))).sort((a, b) => a - b);

export type DailyOverviewTotalizersPeriod = {
    startDateTime: string;
    endDateTime: string;
    dayKey: string;
};

export const buildDailyOverviewTotalizersQueryKey = (
    powerStationIds: number[],
    dayKey: string | null
) => [
    DAILY_OVERVIEW_TOTALIZERS_QUERY_KEY_ROOT,
    powerStationIds,
    dayKey,
] as const;

export const serializeDailyOverviewTotalizersQueryKey = (
    powerStationIds: number[],
    dayKey: string | null
): string => JSON.stringify(buildDailyOverviewTotalizersQueryKey(powerStationIds, dayKey));

export const hasValidDailyOverviewTotalizersSnapshot = (
    data: DailyOverviewTotalizersPayload | undefined
): boolean => !!data && isValidDailyOverviewTotalizersPayload(data);

const isRefreshCancellation = (error: unknown): boolean => {
    if (isCancelledError(error)) {
        return true;
    }
    return typeof axios.isCancel === "function" && axios.isCancel(error);
};

const createChromeTransportError = (): Error =>
    Object.assign(new Error("Network Error"), {code: "ERR_NETWORK"});

const fetchDailyOverviewTotalizers = async (
    powerStationIds: number[],
    startDateTime: string,
    endDateTime: string
): Promise<DailyOverviewTotalizersPayload> => {
    const response = await axios.post<{ data: DailyOverviewTotalizersPayload }>(
        API_URL,
        {powerStationIds},
        {
            params: {startDateTime, endDateTime},
            timeout: 30000,
        }
    );

    if (response.status === 0) {
        return Promise.reject(createChromeTransportError());
    }

    if (!isValidDailyOverviewTotalizersPayload(response.data?.data)) {
        return Promise.reject(new Error("Daily overview totalizers response is incomplete."));
    }

    return response.data.data;
};

export type UseDailyOverviewTotalizersQueryOptions = {
    powerStationIds: Array<number | string>;
    period: DailyOverviewTotalizersPeriod | null;
    refreshGeneration: number;
    enabled?: boolean;
};

type AutomaticFailureState = {scope: string; count: number};

export const useDailyOverviewTotalizersQuery = ({
    powerStationIds,
    period,
    refreshGeneration,
    enabled = true,
}: UseDailyOverviewTotalizersQueryOptions) => {
    const {enqueueSnackbar} = useSnackbar();
    const normalizedIds = normalizePowerStationIds(powerStationIds);
    const dayKey = period?.dayKey ?? null;
    const queryKey = buildDailyOverviewTotalizersQueryKey(normalizedIds, dayKey);
    const serializedQueryScope = serializeDailyOverviewTotalizersQueryKey(normalizedIds, dayKey);
    const queryEnabled = enabled
        && normalizedIds.length > 0
        && !!period?.startDateTime
        && !!period?.endDateTime;

    // O período fica fora da key: cada execução do queryFn lê o ciclo atual.
    const periodRef = useRef(period);
    periodRef.current = period;
    const queryEnabledRef = useRef(queryEnabled);
    queryEnabledRef.current = queryEnabled;
    const queryScopeRef = useRef(serializedQueryScope);
    queryScopeRef.current = serializedQueryScope;
    const automaticInFlightRef = useRef(false);

    // O contador pertence ao escopo (ids + dayKey): trocar o escopo o zera.
    const [automaticFailures, setAutomaticFailures] = useState<AutomaticFailureState>(() => ({
        scope: serializedQueryScope,
        count: 0,
    }));
    const consecutiveAutomaticFailures = automaticFailures.scope === serializedQueryScope
        ? automaticFailures.count
        : 0;

    const query = useQuery({
        queryKey,
        queryFn: () => {
            const currentPeriod = periodRef.current;
            if (!currentPeriod) {
                return Promise.reject(new Error("Daily overview totalizers period is undefined."));
            }
            return fetchDailyOverviewTotalizers(
                normalizedIds,
                currentPeriod.startDateTime,
                currentPeriod.endDateTime
            );
        },
        enabled: queryEnabled,
        retry: false,
        onSuccess: () => {
            setAutomaticFailures((previous) => (
                previous.scope === serializedQueryScope && previous.count === 0
                    ? previous
                    : {scope: serializedQueryScope, count: 0}
            ));
        },
        onError: (error: unknown) => {
            if (automaticInFlightRef.current || isRefreshCancellation(error)) {
                return;
            }
            const axiosError = error as {response?: {data?: {message?: string}}; message?: string};
            const errorMessage =
                axiosError?.response?.data?.message
                || axiosError?.message
                || "Erro ao carregar totalizadores da Visão Diária";
            enqueueSnackbar(errorMessage, {variant: "error", autoHideDuration: 3000});
        },
    });

    const {refetch} = query;

    // Declarado depois do useQuery para que o refetch use as opções (key) já atualizadas.
    useEffect(() => {
        if (refreshGeneration < 1 || !queryEnabledRef.current || automaticInFlightRef.current) {
            return;
        }
        automaticInFlightRef.current = true;
        const scopeAtStart = queryScopeRef.current;
        refetch({throwOnError: true})
            .catch((error: unknown) => {
                if (isRefreshCancellation(error) || queryScopeRef.current !== scopeAtStart) {
                    return;
                }
                setAutomaticFailures((previous) => ({
                    scope: scopeAtStart,
                    count: (previous.scope === scopeAtStart ? previous.count : 0) + 1,
                }));
            })
            .finally(() => {
                automaticInFlightRef.current = false;
            });
    }, [refreshGeneration, refetch]);

    return {
        ...query,
        consecutiveAutomaticFailures,
        maxPreservedRefreshFailures: MAX_PRESERVED_REFRESH_FAILURES,
        hasValidSnapshot: hasValidDailyOverviewTotalizersSnapshot(query.data),
        preserveExpired: consecutiveAutomaticFailures > MAX_PRESERVED_REFRESH_FAILURES,
    };
};
